/* Ask the Pack. The wolf is the one launcher on the site. What it opens depends on one theme setting
   (Integrations, "Ask the Pack opens the chat app directly"):

   On: the chat app's window (Gorgias), where its AI agent answers and hands over to a person. That window takes
   its name and its message hint from this section's settings, and its corners from the layout, so it reads as
   Ask the Pack. The chat button under "Talk to a person" opens it and asks for a person. Text typed into a
   search box whose "Ask the Pack" link is tapped is waiting in its message box. If the chat app has not loaded
   yet the wolf dims and waits a moment for it; if it never loads (blocked, offline), the guide below opens.

   Off: this file's own guide. Keyword answers from the section's blocks, Spanish answered in English under a Spanish line saying so, the
   hard-days list to 988 with nothing for sale, everything else handed to a person. "Talk to a person" stays in
   the panel first: it says when a person replies and offers the chat, a text and WhatsApp, and from then on the
   message box writes to a person, not to the guide. The chat itself runs in the chat app's window, which cannot
   be placed inside this panel: the panel closes as that window opens, and what was typed here is waiting in its
   message box.

   The page's kind (knee, eye, face, hand or any, set by the section from the product or collection) picks the
   answers: an answer written for that kind wins over the general one with the same key, and answers written for
   other kinds are never used here. Chips can name a kind too ("product:knee").

   Either way: a dot on the wolf says a reply is waiting in the chat, and the wolf then opens the chat window.
   While that window is open the wolf is hidden, so it does not show under the window; the window's own close
   button, or the Escape key, closes it, and the wolf comes back. A launcher marked data-ask-person asks for a
   person; one marked data-ask-fill="#field" carries whatever is typed in that field. */
import { $, layers, track, utm } from '@theme/utils';

const panel = $('#ask-panel');
if (panel) {
  const cfg = JSON.parse(panel.querySelector('[data-ask-config]').textContent);
  const log = panel.querySelector('[data-ask-log]'), chips = panel.querySelector('[data-ask-chips]'), input = panel.querySelector('#askq');
  const fab = $('.ask-fab');
  const ctx = panel.dataset.ctx || 'general';
  const kind = panel.dataset.kind || 'any';
  const useChat = !!window.Theme.settings.askHandoffChat;
  const chatFirst = useChat && document.body.dataset.askChat === 'first';
  const name = panel.getAttribute('aria-label') || 'Ask the Pack';
  const replyNode = panel.querySelector('.ask-ft .muted');
  const replyLine = replyNode ? replyNode.textContent.trim() : '';
  const askHint = input.getAttribute('placeholder') || '';
  const personAsk = window.Theme.strings.talkToPerson || 'Talk to a person';
  const chatCss = document.getElementById('ask-chat-css');
  const edge = String.fromCharCode(92) + 'b';
  const esWords = (cfg.esHint || []).map((w) => w.trim()).filter(Boolean);
  const esRe = new RegExp('[¿¡]' + (esWords.length ? '|' + edge + '(' + esWords.join('|') + ')' + edge : ''), 'i');
  const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const linkify = (s) => esc(s).replace(new RegExp('[*][*](.+?)[*][*]', 'g'), '<b>$1</b>');
  /* the answers this page may give: those written for its kind first, then the general ones */
  const kindsOf = (x) => (x.kinds || []).map((k) => String(k || '').trim()).filter(Boolean);
  const answers = (cfg.list || [])
    .filter((x) => { const ks = kindsOf(x); return !ks.length || ks.includes(kind); })
    .sort((x, y) => (kindsOf(y).length ? 1 : 0) - (kindsOf(x).length ? 1 : 0));
  const byKey = (k) => answers.find((x) => x.key === k) || null;
  const KEY = 'nocry-chat';
  const born = Date.now();
  let chat = null, chatDead = false, unread = 0, pending = '', personMode = false;
  const chatApi = () => chat || (useChat && window.GorgiasChat && typeof window.GorgiasChat.open === 'function' ? window.GorgiasChat : null);
  const chatOpen = () => { const c = chatApi(); try { return !!(c && typeof c.isOpen === 'function' && c.isOpen()); } catch (e) { return false; } };
  const chatShut = () => { try { chatApi().close(); } catch (e) { /* it stays open */ } setTimeout(sync, 400); };
  const started = () => { try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; } };

  /* while the chat window is open the wolf is hidden. The chat app says when the window opens and closes; a look
     every second while it is open brings the wolf back if the window went away without saying so, so the page is
     never left without its launcher */
  let watch = 0;
  function sync(state) {
    if (!fab) return;
    const openNow = typeof state === 'boolean' ? state : chatOpen();
    fab.hidden = openNow;
    if (openNow && !watch) watch = setInterval(sync, 1000);
    if (!openNow && watch) { clearInterval(watch); watch = 0; }
  }
  /* the styles the layout prints for the chat window (theme setting) go into that window's own document; the
     window is built late and can be rebuilt, so this looks for it for a while after each call */
  function paint() {
    const css = chatCss ? chatCss.textContent.trim() : '';
    if (!css) return;
    let n = 0;
    const t = setInterval(() => {
      n += 1;
      let d = null;
      try { const f = document.querySelector('#gorgias-chat-container #chat-window, iframe#chat-window'); d = f ? f.contentDocument : null; } catch (e) { d = null; }
      if (d && d.head && d.head.children.length) {
        if (!d.getElementById('nocry-chat-css')) { const st = d.createElement('style'); st.id = 'nocry-chat-css'; st.textContent = css; d.head.appendChild(st); }
        clearInterval(t);
      } else if (n > 60) clearInterval(t);
    }, 250);
  }
  /* the chat app loads on its own schedule; `chat` is set once its API answers */
  function chatReady() {
    const c = window.GorgiasChat;
    if (!c || typeof c.open !== 'function') return;
    chat = c;
    try {
      const words = { headerText: name };
      if (askHint) words.inputPlaceholder = askHint;
      if (typeof c.updateTexts === 'function') c.updateTexts(words);
    } catch (e) { /* the window keeps its own words */ }
    paint();
    if (typeof c.on !== 'function') return;
    c.on('unreadCount', (d) => {
      unread = Number(d && typeof d === 'object' ? d.count : d) || 0;
      if (fab) fab.classList.toggle('has-unread', unread > 0);
    });
    c.on('message:sent', () => { try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* no storage: the person step shows each time */ } });
    c.on('widget:opened', () => { sync(true); paint(); });
    c.on('widget:closed', () => sync(false));
    sync();
  }
  function chatBoot() {
    let ready = null;
    try { ready = window.GorgiasChat.init(); } catch (e) { /* no init on this build: use it as it is */ }
    Promise.resolve(ready).catch(() => null).then(chatReady);
  }
  if (useChat) { if (window.GorgiasChat) chatBoot(); else window.addEventListener('gorgias-widget-loaded', chatReady, { once: true }); }
  /* chat-first: the chat app may still be loading when the wolf is tapped. The wolf dims and waits up to four
     seconds; once the wait has failed, or the page is older than twelve seconds without it, nobody waits again */
  function whenChat() {
    return new Promise((resolve) => {
      if (chat) { resolve(true); return; }
      if (chatDead || Date.now() - born > 12000) { chatDead = true; resolve(false); return; }
      if (fab) fab.classList.add('is-wait');
      const t0 = Date.now();
      const t = setInterval(() => {
        if (!chat && Date.now() - t0 < 4000) return;
        clearInterval(t);
        if (fab) fab.classList.remove('is-wait');
        if (!chat) chatDead = true;
        resolve(!!chat);
      }, 100);
    });
  }

  function add(html, who) { const d = document.createElement('div'); d.className = 'msg ' + who; d.innerHTML = html; log.appendChild(d); log.scrollTop = log.scrollHeight; return d; }
  function acts(list) {
    if (!list || !list.length) return '';
    return '<div class="msg-acts">' + list.map((a) => `<a class="btn btn-line btn-sm" href="${esc(a[1])}"${a[1] === '#human' ? ' data-ask-human' : ''}${a[1] === '#finder' ? ' data-ask-close' : ''}>${esc(a[0])}</a>`).join('') + '</div>';
  }
  function setPerson(on) { personMode = on; input.setAttribute('placeholder', on ? personAsk : askHint); }
  /* the chat app's window takes over from the panel. q: text to leave in its message box. person: a person was
     asked for, so the window opens on its conversation (its offline form out of hours). ask: say so in the
     conversation too, once per visit, so the AI agent hands over at once */
  function openChat(q, person, ask) {
    const c = chatApi();
    if (!c) return false;
    try { c.open(); } catch (e) { return false; }
    setTimeout(sync, 400);
    close();
    /* outside business hours the chat app's conversation page opens blank (no message box, no topics), so a
       person asked for then opens on its home screen, where "Send us a message" leads to its offline form */
    let live = true;
    try { live = typeof c.isChatOnline !== 'function' || !!c.isChatOnline(); } catch (e) { live = true; }
    try {
      if (person && typeof c.setPage === 'function') c.setPage(live ? 'conversation' : 'homepage');
      if (live && ask && !q && !started() && typeof c.sendMessage === 'function') c.sendMessage(personAsk);
      else if (q && typeof c.setCurrentInput === 'function') c.setCurrentInput(q);
    } catch (e) { /* the window keeps its own first screen; the question is still in the guide's log */ }
    track('assistant_chat_opened', { ctx, kind });
    return true;
  }
  /* text and WhatsApp links are printed by the layout (template#ask-channels) from the theme settings;
     the question, when there is one, becomes the first words of the message */
  function channelLinks(q) {
    const tpl = document.getElementById('ask-channels');
    if (!tpl || !tpl.content) return '';
    const enc = q ? encodeURIComponent(q) : '';
    return Array.from(tpl.content.querySelectorAll('a[data-contact-channel]')).map((a) => {
      const kind = a.dataset.contactChannel;
      const href = a.getAttribute('href') + (enc ? (kind === 'whatsapp' ? '?text=' : '?&body=') + enc : '');
      return `<a class="btn btn-line btn-sm" href="${esc(href)}"${kind === 'whatsapp' ? ' target="_blank" rel="noopener"' : ''} data-contact-channel="${esc(kind)}">${esc(a.textContent.trim())}</a>`;
    }).join('');
  }
  /* the person step of the guide. direct: the visitor asked for a person; otherwise a question went unanswered */
  function handoff(q, direct) {
    track('assistant_handoff', { q, kind });
    if (direct && started() && openChat(q, true, false)) return;
    if (direct) setPerson(true);
    const last = log.lastElementChild;
    if (direct && last && last.dataset.person) return;
    pending = q || '';
    const lead = direct && replyLine ? esc(replyLine) : linkify(cfg.handoff);
    const links = channelLinks(pending);
    let html;
    if (chatApi()) {
      html = `<div class="msg-acts"><button type="button" class="btn btn-dark btn-sm" data-ask-chat>Chat</button>${links}</div>`;
    } else if (cfg.endpoint) {
      html = (links ? `<div class="msg-acts">${links}</div>` : '')
        + `<form class="ask-hand" data-form="webhook" data-name="ask-the-pack" data-tag="pre-sale" data-endpoint="${esc(cfg.endpoint)}"><input type="hidden" name="question" value="${esc(pending)}"><input type="hidden" name="page" value="${esc(location.href)}"><input type="email" name="email" required placeholder="${esc(window.Theme.strings.yourEmail || 'Your email')}" aria-label="${esc(window.Theme.strings.emailLabel || 'Email')}"><button class="btn btn-dark btn-sm" type="submit">${esc(window.Theme.strings.sendToPerson || 'Send to a person')}</button><p class="ok small"><b>${esc(cfg.handoffDone)}</b></p><p class="form-err"></p></form>`;
    } else {
      html = `<div class="msg-acts">${links}<a class="btn btn-dark btn-sm" href="mailto:${esc(cfg.email)}?subject=${encodeURIComponent(window.Theme.strings.questionSubject || 'Question from the site')}&body=${encodeURIComponent(pending)}">${esc(window.Theme.strings.emailPerson || 'Email a person')}</a></div>`;
    }
    add(lead + html, 'bot').dataset.person = '1';
  }
  /* the hard-days list matches anywhere in the question, with curly and straight apostrophes treated alike and
     apostrophes optional ("cant go on" and "can't go on" both match) */
  const plain = (s) => String(s || '').toLowerCase().replace(/[‘’ʼ`´']/g, '').replace(/\s+/g, ' ').trim();
  const hardList = (cfg.hard || []).map(plain).filter(Boolean);
  const isHard = (q) => { const ql = plain(q); return hardList.some((t) => ql.indexOf(t) > -1); };
  /* the hard-days reply: the lines from the section, then 988 one tap away and a person if they want one.
     Nothing for sale, so the chips go */
  function hardday() {
    track('assistant_hard_day', {});
    add(linkify(cfg.hardReply) + acts([[window.Theme.strings.call988 || 'Call 988', 'tel:988'], [window.Theme.strings.text988 || 'Text 988', 'sms:988'], [personAsk, '#human']]), 'bot care');
    chips.innerHTML = '';
  }
  /* which answer fits: a keyword has to begin a word of the question; a keyword of three letters or fewer, or one
     typed with a space after it, has to be the whole word; when several answers fit, the longest keyword wins, and
     on a tie the answer written for this page's kind wins */
  const words = (s) => ' ' + String(s || '').toLowerCase().split('').map((ch) => (ch.toLowerCase() !== ch.toUpperCase() || (ch >= '0' && ch <= '9') ? ch : ' ')).join('').split(' ').filter(Boolean).join(' ') + ' ';
  function match(q) {
    const qn = words(q);
    let best = null, len = 0;
    for (const ans of answers) {
      for (const w of (ans.k || [])) {
        const raw = String(w || '').toLowerCase(), kn = words(raw), size = kn.length - 2;
        if (size < 1 || size <= len) continue;
        const whole = size <= 3 || raw.endsWith(' ');
        if (qn.indexOf(whole ? kn : kn.slice(0, -1)) > -1) { best = ans; len = size; }
      }
    }
    return best;
  }
  function answer(q, key) {
    track('assistant_question', { q, ctx, kind });
    if (isHard(q)) { hardday(); return; }
    if (key === 'finder') {
      const href = document.querySelector('#finder') ? '#finder' : (document.querySelector('[data-finder-url]')?.dataset.finderUrl || '/collections/knee-pads#finder');
      add(linkify(cfg.finderText) + ` <a class="link" href="${href}" data-ask-close>${esc(cfg.finderLabel)}</a>`, 'bot'); return;
    }
    if (key === 'human') { handoff('', true); return; }
    /* support replies in English only: a question in Spanish gets the English answer under one Spanish line
       saying so (the section's "es_note"), never a Spanish answer that suggests Spanish service */
    const esNote = esRe && esRe.test(q) && cfg.esNote ? `<span class="es-note" lang="es">${esc(cfg.esNote)}</span><br>` : '';
    const hit = (key && byKey(key)) || match(q);
    if (!hit) { if (esNote) add(esNote, 'bot'); handoff(q, false); return; }
    track('assistant_answered', { ctx, kind, key: hit.key });
    add(esNote + linkify(hit.a) + acts(hit.act), 'bot');
  }
  function renderChips() {
    const here = [ctx, ctx + ':' + kind];
    chips.innerHTML = (cfg.chips || []).filter((c) => (c.ctx || []).map((x) => x.trim()).some((x) => here.includes(x))).filter((c) => c.key === 'finder' || c.key === 'human' || byKey(c.key)).map((c) => `<button type="button" class="chip" data-ask-q="${esc(c.label)}" data-ask-key="${esc(c.key)}">${esc(c.label)}</button>`).join('');
  }
  function open() { layers.open('ask', panel); track('assistant_opened', { ctx, kind, ...utm() }); }
  function close() { layers.close('ask'); }
  /* the guide's panel. v: a question to ask it at once. person: open on the person step */
  function guide(v, person) {
    if (chatOpen()) chatShut();
    open();
    if (v) { setPerson(false); add(esc(v), 'me'); answer(v, null); return; }
    if (person) handoff('', true);
  }
  /* every launcher ends here: the wolf, "Ask the Pack" links and buttons */
  function go(v, person) {
    if (chatFirst) { whenChat().then((ok) => { if (!(ok && openChat(v, person, person))) guide(v, person); }); return; }
    if ((unread > 0 || (person && started())) && openChat(v, person, false)) return;
    guide(v, person);
  }
  function launch(el) {
    if (el && el === fab && chatOpen()) { chatShut(); return; }
    const fill = el ? el.getAttribute('data-ask-fill') : '';
    let v = '';
    if (fill) { try { const src = document.querySelector(fill); v = src && src.value ? src.value.trim() : ''; } catch (e) { /* no such field */ } }
    go(v, !!el && el.hasAttribute('data-ask-person'));
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ask-open]');
    if (opener) { e.preventDefault(); launch(opener); return; }
    if (!panel.contains(e.target)) return;
    if (e.target === panel || e.target.closest('[data-ask-close]')) { close(); return; }
    if (e.target.closest('[data-ask-chat]')) { e.preventDefault(); openChat(pending, true, false); return; }
    const c = e.target.closest('[data-ask-q]'); if (c) { setPerson(false); add(esc(c.dataset.askQ), 'me'); answer(c.dataset.askQ, c.dataset.askKey); return; }
    if (e.target.closest('[data-ask-human]')) { e.preventDefault(); add(esc(personAsk), 'me'); handoff(input.value.trim(), true); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && chatOpen()) chatShut(); });
  panel.querySelector('[data-ask-form]').addEventListener('submit', (e) => {
    e.preventDefault(); const q = input.value.trim(); if (!q) return;
    add(esc(q), 'me'); input.value = '';
    /* after "Talk to a person" the box writes to a person; the hard-days list still comes first */
    if (personMode && !isHard(q)) { if (!openChat(q, true, false)) handoff(q, true); return; }
    answer(q, null);
  });
  renderChips();
  window.NOCRY = Object.assign(window.NOCRY || {}, { ask: { open: () => go('', false), close, person: () => go('', true) } });
}
